/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import Moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCircleCheck,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import ScreeningService from '../../../../../Services/ScreeningService';
import XpektorButton from '../../../../../Components/XpektorButton/XpektorButton';
import DataPanel from '../../../../../Components/DataPanel/DataPanel';
import './CrimeRecordView.scss';

function ResultEntry({ entry, index, onDownloadClick }) {
  return (
    <div className="list-entry">
      <p>{`${index + 1}.`}</p>
      <p className="s-text breakable">
        <b>Kategori: </b>
        {entry.shelf}
      </p>
      <p className="s-text">
        <b>Dokumenttyp: </b>
        {entry.docType}
      </p>
      <XpektorButton
        content={entry.downloadable ? 'Hämta dokument' : 'Ej nedladdningsbart'}
        onClick={() => {
          if (entry.downloadable) {
            onDownloadClick();
          }
        }}
        disabled={!entry.downloadable}
      />
    </div>
  );
}

function ScreeningEntry({ entry, onClick, selected }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      className={`list-entry ${selected ? 'selected-entry' : ''} ${
        isHovered && !selected ? 'hovered-entry' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <p className="s-text">
        <b>
          {entry.organizationNumber
            ? 'Organisation'
            : entry.name
            ? 'Person'
            : 'N/A'}
        </b>
      </p>
      <p>{entry.name ?? 'N/A'}</p>
      {entry.results.length > 0 ? (
        <div className="icon-text-row">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="icon warning"
          />
          <p>{`${entry.results.length} resultat`}</p>
        </div>
      ) : (
        <div className="icon-text-row">
          <FontAwesomeIcon icon={faCircleCheck} className="icon ok" />
          <p>Inga resultat</p>
        </div>
      )}
    </button>
  );
}

function RecordEntry({ entry, onClick, selected }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <button
      type="button"
      className={`list-entry ${selected ? 'selected-entry' : ''} ${
        isHovered && !selected ? 'hovered-entry' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <p>
        <b>
          Skanning -{' '}
          {Moment.utc(entry.screeningDate).local().format('YYYY-MM-DD HH:mm')}
        </b>
      </p>

      {entry.crimeScreenings.length === 0 ? (
        <div className="icon-text-row">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="icon error"
          />
          <p>Något gick fel</p>
        </div>
      ) : (
        <p>{`Resultat på ${
          entry.crimeScreenings.filter((screening) => screening.hits === true)
            .length
        } / ${entry.crimeScreenings.length} skannade.`}</p>
      )}
    </button>
  );
}

export default function CrimeRecordView({ clientId, isImpersonating }) {
  const [screeningRecords, setScreeningRecords] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});
  const [selectedScreening, setSelectedScreening] = useState({});

  useEffect(() => {
    const getScreeningRecords = async () => {
      await ScreeningService.getClientCrimeScreenings(clientId)
        .then((response) => {
          setScreeningRecords(response.data);
        })
        .catch(() => {});
    };

    if (clientId) {
      getScreeningRecords();
    }
  }, [clientId]);

  async function GetDocumentLink(resultId) {
    await ScreeningService.getCrimeDocument(clientId, resultId)
      .then((response) => {
        window.open(response.data, '_blank', 'noopener,noreferrer');
      })
      .catch(() => {});
  }

  const StartScreening = async () => {
    setIsScanning(true);
    await ScreeningService.startClientCrimeScreening(clientId)
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        ScreeningService.getClientCrimeScreenings(clientId)
          .then((recordsResponse) => {
            setScreeningRecords(recordsResponse.data);
          })
          .catch(() => {});
        setIsScanning(false);
      });
  };

  const onRecordClick = (record) => {
    if (
      Object.keys(selectedRecord).length > 0 &&
      selectedRecord.id === record.id
    ) {
      setSelectedRecord({});
    } else {
      setSelectedRecord(record);
    }
    setSelectedScreening({});
  };

  const onScreeningClick = (screening) => {
    if (
      Object.keys(selectedScreening).length > 0 &&
      selectedScreening.id === screening.id
    ) {
      setSelectedScreening({});
    } else {
      setSelectedScreening(screening);
    }
  };

  return (
    <DataPanel
      title="Rättslig Information"
      icon={faCircleQuestion}
      iconStart
      iconHoverContent={
        <p>
          Här kan du göra en sökning efter rättslig information på företaget och
          samtliga aktiva styrelsemedlemmar. <br />
          <br />
          Kostnad tillkommer per skanning på nästa faktura.
        </p>
      }
    >
      <div className="crime-record-view">
        <div className="view-column">
          <p className="view-title">Skanningar</p>
          <div
            className={`entry-list ${
              screeningRecords.length === 0 ? 'empty-list' : ''
            }`}
          >
            {screeningRecords.length > 0 ? (
              screeningRecords
                .sort(
                  (a, b) =>
                    new Date(b.screeningDate) - new Date(a.screeningDate)
                )
                .map((record) => {
                  return (
                    <RecordEntry
                      key={record.id}
                      entry={record}
                      onClick={() => onRecordClick(record)}
                      selected={selectedRecord?.id === record.id}
                    />
                  );
                })
            ) : (
              <p className="no-content-entry">Inga skanningar har gjorts</p>
            )}
          </div>
          <div className="column-footer">
            <XpektorButton
              content="Skanna företag och styrelse"
              onClick={StartScreening}
              disabled={isImpersonating}
              loading={isScanning}
            />
            <p>
              <i>Kostnad tillkommer per skanning på nästa faktura.</i>
            </p>
          </div>
        </div>
        {Object.keys(selectedRecord).length > 0 ? (
          <div className="view-column">
            <p className="view-title">Skannade entiteter</p>
            <div
              className={`entry-list ${
                selectedRecord.crimeScreenings.length === 0 ? 'empty-list' : ''
              }`}
            >
              {selectedRecord.crimeScreenings.length > 0 ? (
                selectedRecord.crimeScreenings.map((screening) => {
                  return (
                    <ScreeningEntry
                      key={screening.id}
                      entry={screening}
                      onClick={() => onScreeningClick(screening)}
                      selected={selectedScreening?.id === screening.id}
                    />
                  );
                })
              ) : (
                <p className="no-content-entry">
                  Något gick fel - Inga skanningar gjordes
                </p>
              )}
            </div>
          </div>
        ) : (
          ''
        )}
        {Object.keys(selectedScreening).length > 0 ? (
          <div className="view-column">
            <p className="view-title">Resultat</p>
            <div
              className={`entry-list ${
                selectedScreening.results.length === 0 ? 'empty-list' : ''
              }`}
            >
              {selectedScreening.results.length > 0 ? (
                selectedScreening.results.map((result, index) => {
                  return (
                    <ResultEntry
                      key={result.id}
                      index={index}
                      entry={result}
                      onDownloadClick={() => {
                        GetDocumentLink(result.id);
                      }}
                    />
                  );
                })
              ) : (
                <p className="no-content-entry">Inga resultat</p>
              )}
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
    </DataPanel>
  );
}
